import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import getDataTableStyles from '../../utils/tableStyles';
import { Modal, Form, Spinner, Row, Col, Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import axiosInstance from '../../api/axios';
import { MapContainer, TileLayer, Polygon, useMapEvents, Marker, useMap, Tooltip, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default leaflet icons not showing in React Leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});

const BARINAS_CENTER = [8.6226, -70.2075];

export const ZONE_PALETTE = [
    { color: '#2563eb', bg: 'rgba(37, 99, 235, 0.12)', border: '#2563eb' }, // Blue
    { color: '#059669', bg: 'rgba(5, 150, 105, 0.12)', border: '#059669' }, // Emerald
    { color: '#d97706', bg: 'rgba(217, 119, 6, 0.12)', border: '#d97706' }, // Amber
    { color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.12)', border: '#7c3aed' }, // Purple
    { color: '#db2777', bg: 'rgba(219, 39, 119, 0.12)', border: '#db2777' }, // Pink
    { color: '#0891b2', bg: 'rgba(8, 145, 178, 0.12)', border: '#0891b2' }, // Cyan
    { color: '#ea580c', bg: 'rgba(234, 88, 12, 0.12)', border: '#ea580c' }, // Orange
    { color: '#dc2626', bg: 'rgba(220, 38, 38, 0.12)', border: '#dc2626' }, // Red
    { color: '#4f46e5', bg: 'rgba(79, 70, 229, 0.12)', border: '#4f46e5' }, // Indigo
    { color: '#65a30d', bg: 'rgba(101, 163, 13, 0.12)', border: '#65a30d' }, // Lime
    { color: '#c026d3', bg: 'rgba(192, 38, 211, 0.12)', border: '#c026d3' }, // Fuchsia
    { color: '#0d9488', bg: 'rgba(13, 148, 136, 0.12)', border: '#0d9488' }, // Teal
];

export const getZoneColor = (index) => ZONE_PALETTE[index % ZONE_PALETTE.length];

const initialForm = {
    name: '',
    code: '',
    vendedor_id: '',
    mercaderista_id: '',
    polygon: []
};

// Helper to normalize polygon to an array of [lat, lng] pairs
const normalizePolygonPoints = (input) => {
    if (!input) return [];
    
    // If it's a string, try to parse JSON
    if (typeof input === 'string') {
        try {
            input = JSON.parse(input);
        } catch {
            return [];
        }
    }
    
    // If it's a GeoJSON object { type: 'Polygon', coordinates: [...] }
    if (input && typeof input === 'object' && !Array.isArray(input)) {
        if (input.coordinates && Array.isArray(input.coordinates) && Array.isArray(input.coordinates[0])) {
            return input.coordinates[0].map(pt => [Number(pt[1]), Number(pt[0])]);
        }
        return [];
    }
    
    // If it's an Array
    if (Array.isArray(input)) {
        // Could be GeoJSON coordinates array [[[lng, lat], ...]]
        if (input.length > 0 && Array.isArray(input[0]) && Array.isArray(input[0][0])) {
            return input[0].map(pt => [Number(pt[1]), Number(pt[0])]);
        }
        // Array of [lat, lng]
        return input
            .filter(pt => Array.isArray(pt) && pt.length >= 2 && !isNaN(Number(pt[0])) && !isNaN(Number(pt[1])))
            .map(pt => [Number(pt[0]), Number(pt[1])]);
    }
    
    return [];
};

// Component to dynamically position and fit the map view in the editor
function MapViewController({ polygon }) {
    const map = useMap();
    useEffect(() => {
        const validPoints = normalizePolygonPoints(polygon);
        if (validPoints && validPoints.length > 0) {
            const bounds = L.latLngBounds(validPoints);
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
        } else {
            map.setView(BARINAS_CENTER, 13);
        }
    }, [polygon, map]);
    return null;
}

// Component to fit all zones or a selected zone in the global multi-zone map
function MultiZoneMapViewController({ zones, selectedZoneId }) {
    const map = useMap();
    useEffect(() => {
        if (selectedZoneId) {
            const target = zones.find(z => String(z.id) === String(selectedZoneId));
            if (target) {
                const pts = normalizePolygonPoints(target.polygon);
                if (pts.length > 0) {
                    map.fitBounds(L.latLngBounds(pts), { padding: [50, 50], maxZoom: 15 });
                    return;
                }
            }
        }

        const allPoints = [];
        zones.forEach(z => {
            const pts = normalizePolygonPoints(z.polygon);
            if (pts.length > 0) {
                allPoints.push(...pts);
            }
        });

        if (allPoints.length > 0) {
            map.fitBounds(L.latLngBounds(allPoints), { padding: [40, 40], maxZoom: 14 });
        } else {
            map.setView(BARINAS_CENTER, 12);
        }
    }, [zones, selectedZoneId, map]);

    return null;
}

// Component to handle clicks on the map
function MapClickHandler({ setPolygon, polygon }) {
    useMapEvents({
        click(e) {
            const current = normalizePolygonPoints(polygon);
            setPolygon([...current, [e.latlng.lat, e.latlng.lng]]);
        }
    });
    return null;
}

// Helper to sort points in clockwise order around their center
const getOrderedPolygon = (points) => {
    const validPoints = normalizePolygonPoints(points);
    if (!Array.isArray(validPoints) || validPoints.length < 3) return validPoints || [];
    
    const center = validPoints.reduce((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0]);
    center[0] /= validPoints.length;
    center[1] /= validPoints.length;
    
    return [...validPoints].sort((a, b) => {
        const angleA = Math.atan2(a[0] - center[0], a[1] - center[1]);
        const angleB = Math.atan2(b[0] - center[0], b[1] - center[1]);
        return angleA - angleB;
    });
};

const ZonesManager = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isDark } = useContext(ThemeContext) || {};
    const [zones, setZones] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [mercaderistas, setMercaderistas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [vendorFilter, setVendorFilter] = useState('');
    const [mercaderistaFilter, setMercaderistaFilter] = useState('');
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'map'
    const [selectedMapZoneId, setSelectedMapZoneId] = useState(null);
    const [conflictError, setConflictError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Territorial sync feedback modal state
    const [syncResult, setSyncResult] = useState(null);
    const [showSyncModal, setShowSyncModal] = useState(false);

    const selectedVendorAssignedOtherZone = useMemo(() => {
        if (!formData.vendedor_id) return null;
        return (zones || []).find(z => 
            String(z.vendedor_id) === String(formData.vendedor_id) && 
            (!editingId || String(z.id) !== String(editingId))
        );
    }, [zones, formData.vendedor_id, editingId]);

    const selectedMerchAssignedOtherZone = useMemo(() => {
        if (!formData.mercaderista_id) return null;
        return (zones || []).find(z => 
            String(z.mercaderista_id) === String(formData.mercaderista_id) && 
            (!editingId || String(z.id) !== String(editingId))
        );
    }, [zones, formData.mercaderista_id, editingId]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [zonesRes, sellersRes, mercaderistasRes] = await Promise.all([
                axiosInstance.get('/admin/zones').catch(err => {
                    console.error('Error cargando zonas:', err);
                    return { data: { data: [] } };
                }),
                axiosInstance.get('/admin/sellers').catch(async () => {
                    return axiosInstance.get('/admin/users?rol=vendedor&per_page=100').catch(() => ({ data: { data: [] } }));
                }),
                axiosInstance.get('/admin/merchandisers').catch(async () => {
                    return axiosInstance.get('/admin/users?rol=mercaderista&per_page=100').catch(() => ({ data: { data: [] } }));
                })
            ]);

            setZones(zonesRes.data?.data || []);

            const sellersList = sellersRes.data?.data?.data || sellersRes.data?.data || [];
            setVendedores(Array.isArray(sellersList) ? sellersList : []);

            const mercList = mercaderistasRes.data?.data?.data || mercaderistasRes.data?.data || [];
            setMercaderistas(Array.isArray(mercList) ? mercList : []);
        } catch (err) {
            console.error('Error cargando datos de zonas:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = (zone = null) => {
        setConflictError(null);
        if (zone) {
            setEditingId(zone.id);
            setFormData({
                name: zone.name || '',
                code: zone.code || '',
                vendedor_id: zone.vendedor_id || '',
                mercaderista_id: zone.mercaderista_id || '',
                polygon: normalizePolygonPoints(zone.polygon)
            });
        } else {
            setEditingId(null);
            setFormData(initialForm);
        }
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de eliminar esta zona?')) {
            try {
                await axiosInstance.delete(`/admin/zones/${id}`);
                fetchData();
            } catch (err) {
                alert(err.response?.data?.message || 'Error eliminando zona');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setConflictError(null);
        try {
            const orderedPolygon = getOrderedPolygon(formData.polygon);
            const payload = {
                ...formData,
                polygon: orderedPolygon,
                vendedor_id: formData.vendedor_id || null,
                mercaderista_id: formData.mercaderista_id || null
            };

            let resp;
            if (editingId) {
                resp = await axiosInstance.put(`/admin/zones/${editingId}`, payload);
            } else {
                resp = await axiosInstance.post('/admin/zones', payload);
            }
            setShowModal(false);
            fetchData();

            const sync = resp.data?.territorial_sync;
            if (sync && (sync.absorbed > 0 || sync.expelled > 0 || sync.reassigned_other > 0)) {
                setSyncResult({
                    zoneName: formData.name,
                    zoneCode: formData.code,
                    vendedorName: vendedores.find(v => String(v.id) === String(formData.vendedor_id))?.nombre_completo || 'Sin vendedor titular',
                    ...sync
                });
                setShowSyncModal(true);
            } else {
                alert('Zona guardada exitosamente.');
            }
        } catch (err) {
            if (err.response?.status === 422) {
                const data = err.response.data;
                if (data.conflicts && data.conflicts.length > 0) {
                    const first = data.conflicts[0];
                    setConflictError(`El polígono invade la geocerca de '${first.name}' (${first.code}) por aprox. ${first.overlap_sqm} m². Por favor ajuste los vértices.`);
                } else if (data.errors) {
                    const errorMessages = Object.values(data.errors).flat().join('\n');
                    setConflictError(errorMessages);
                } else {
                    setConflictError(data.message || 'Error de validación al guardar la zona');
                }
            } else {
                alert(err.response?.data?.message || 'Error guardando la zona');
            }
        } finally {
            setProcessing(false);
        }
    };

    const filteredZones = zones.filter(z => {
        const matchesSearch = !search || 
            z.name?.toLowerCase().includes(search.toLowerCase()) || 
            z.code?.toLowerCase().includes(search.toLowerCase());
        
        const matchesVendor = !vendorFilter || 
            (vendorFilter === 'unassigned' ? !z.vendedor_id : String(z.vendedor_id) === String(vendorFilter));

        const matchesMercaderista = !mercaderistaFilter || 
            (mercaderistaFilter === 'unassigned' ? !z.mercaderista_id : String(z.mercaderista_id) === String(mercaderistaFilter));

        return matchesSearch && matchesVendor && matchesMercaderista;
    });

    const columns = [
        {
            name: t('th_zone_code', 'Código'),
            selector: row => row.code,
            sortable: true,
            width: '120px'
        },
        {
            name: t('th_zone_name', 'Nombre de Zona'),
            selector: row => row.name,
            sortable: true,
        },
        {
            name: t('th_assigned_vendor', 'Vendedor Asignado'),
            selector: row => row.vendedor ? (
                <div className="d-flex align-items-center gap-2">
                    <span className="badge rounded-pill" style={{ backgroundColor: 'rgba(24, 62, 235, 0.1)', color: '#183eeb', border: '1px solid rgba(24, 62, 235, 0.25)', fontSize: '0.75rem', padding: '4px 8px' }}>
                        <i className="mdi mdi-account-tie mr-1"></i> Preventa
                    </span>
                    <span className="fw-semibold">{row.vendedor.nombre_completo}</span>
                </div>
            ) : <span className="text-muted">{t('unassigned', 'Sin Asignar')}</span>,
            sortable: true,
        },
        {
            name: t('th_assigned_merchandiser', 'Mercaderista Asignado'),
            selector: row => row.mercaderista ? (
                <div className="d-flex align-items-center gap-2">
                    <span className="badge rounded-pill" style={{ backgroundColor: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', border: '1px solid rgba(124, 58, 237, 0.25)', fontSize: '0.75rem', padding: '4px 8px' }}>
                        <i className="mdi mdi-store mr-1"></i> Trade
                    </span>
                    <span className="fw-semibold">{row.mercaderista.nombre_completo}</span>
                </div>
            ) : <span className="text-muted">{t('unassigned', 'Sin Asignar')}</span>,
            sortable: true,
        },
        {
            name: t('th_actions', 'Acciones'),
            cell: row => (
                <div className="d-flex gap-2">
                    <button 
                        onClick={() => handleShowModal(row)} 
                        className="btn btn-sm btn-outline-primary rounded-circle" 
                        style={{ width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                        title={t('btn_edit', 'Editar')}
                    >
                        <i className="mdi mdi-pencil"></i>
                    </button>
                    <button 
                        onClick={() => handleDelete(row.id)} 
                        className="btn btn-sm btn-outline-danger rounded-circle" 
                        style={{ width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                        title={t('btn_delete', 'Eliminar')}
                    >
                        <i className="mdi mdi-delete"></i>
                    </button>
                </div>
            ),
            width: '120px',
            ignoreRowClick: true,
        }
    ];

    const removeLastPoint = () => {
        const newPolygon = [...normalizePolygonPoints(formData.polygon)];
        newPolygon.pop();
        setFormData({ ...formData, polygon: newPolygon });
    };

    const clearPolygon = () => {
        setFormData({ ...formData, polygon: [] });
    };

    const orderedPolygon = getOrderedPolygon(formData.polygon);

    return (
        <div>
            {/* Header with Title and View Switcher */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h4 className="card-title mb-1" style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold' }}>
                        {t('zones_territories_title', 'Zonas y Territorios')}
                    </h4>
                    <p className="text-muted small mb-0">
                        {zones.length} zonas configuradas • {zones.filter(z => normalizePolygonPoints(z.polygon).length >= 3).length} con geocerca delimitada
                    </p>
                </div>

                <div className="d-flex align-items-center gap-2">
                    {/* View Switcher: Lista vs Mapa Global */}
                    <div className="btn-group p-1" style={{ backgroundColor: isDark ? '#10172d' : '#f1f5f9', borderRadius: '12px', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                        <button
                            type="button"
                            className={`btn btn-sm px-3 py-2 fw-semibold ${viewMode === 'table' ? 'btn-primary shadow-sm' : ''}`}
                            style={{
                                borderRadius: '9px',
                                border: 'none',
                                backgroundColor: viewMode === 'table' ? '#183eeb' : 'transparent',
                                color: viewMode === 'table' ? '#ffffff' : (isDark ? '#94a3b8' : '#64748b')
                            }}
                            onClick={() => setViewMode('table')}
                        >
                            <i className="mdi mdi-table me-1"></i> Lista
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm px-3 py-2 fw-semibold ${viewMode === 'map' ? 'btn-primary shadow-sm' : ''}`}
                            style={{
                                borderRadius: '9px',
                                border: 'none',
                                backgroundColor: viewMode === 'map' ? '#183eeb' : 'transparent',
                                color: viewMode === 'map' ? '#ffffff' : (isDark ? '#94a3b8' : '#64748b')
                            }}
                            onClick={() => setViewMode('map')}
                        >
                            <i className="mdi mdi-map-marker-multiple me-1"></i> Mapa de Cobertura
                        </button>
                    </div>

                    <button 
                        onClick={() => handleShowModal()} 
                        className="btn btn-primary px-3 py-2" 
                        style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <i className="mdi mdi-plus-circle-outline fs-5"></i>
                        {t('btn_new_zone', 'Nueva Zona')}
                    </button>
                </div>
            </div>

            <div className="card shadow-sm border-0" style={{ borderRadius: '16px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                <div className="card-body p-4">
                    {/* Filters Row */}
                    <Row className="mb-4 g-3 align-items-end">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold small mb-1" style={{ color: isDark ? '#94a3b8' : '#010028' }}>
                                    {t('search_zone_label', 'Buscar Zona')}
                                </Form.Label>
                                <Form.Control 
                                    placeholder={t('search_zone_placeholder', 'Buscar por nombre de zona o código...')} 
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="customer-filter-control w-100 shadow-none"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold small mb-1" style={{ color: isDark ? '#94a3b8' : '#010028' }}>
                                    <i className="mdi mdi-account-tie mr-1 text-primary"></i> {t('filter_by_vendor', 'Filtrar por Vendedor')}
                                </Form.Label>
                                <Form.Select 
                                    value={vendorFilter} 
                                    onChange={e => setVendorFilter(e.target.value)}
                                    className="customer-filter-control w-100 cursor-pointer shadow-none"
                                >
                                    <option value="">{t('all_vendors', 'Todos los Vendedores')}</option>
                                    <option value="unassigned">{t('unassigned_zones', 'Sin Vendedor Asignado')}</option>
                                    {vendedores.map(v => {
                                        const assignedZone = (zones || []).find(z => String(z.vendedor_id) === String(v.id));
                                        const zoneTag = assignedZone ? ` (${assignedZone.code || assignedZone.name})` : ' [Libre]';
                                        return (
                                            <option key={v.id} value={v.id}>{v.nombre_completo}{zoneTag}</option>
                                        );
                                    })}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold small mb-1" style={{ color: isDark ? '#94a3b8' : '#010028' }}>
                                    <i className="mdi mdi-store mr-1" style={{ color: '#7c3aed' }}></i> {t('filter_by_merchandiser', 'Filtrar por Mercaderista')}
                                </Form.Label>
                                <Form.Select 
                                    value={mercaderistaFilter} 
                                    onChange={e => setMercaderistaFilter(e.target.value)}
                                    className="customer-filter-control w-100 cursor-pointer shadow-none"
                                >
                                    <option value="">{t('all_merchandisers', 'Todos los Mercaderistas')}</option>
                                    <option value="unassigned">{t('unassigned_merchandiser_zones', 'Sin Mercaderista Asignado')}</option>
                                    {mercaderistas.map(m => {
                                        const assignedZone = (zones || []).find(z => String(z.mercaderista_id) === String(m.id));
                                        const zoneTag = assignedZone ? ` (${assignedZone.code || assignedZone.name})` : ' [Libre]';
                                        return (
                                            <option key={m.id} value={m.id}>{m.nombre_completo}{zoneTag}</option>
                                        );
                                    })}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* View Mode: Table vs Multi-Zone Map */}
                    {viewMode === 'table' ? (
                        <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                            <DataTable
                                columns={columns}
                                data={filteredZones}
                                pagination
                                responsive
                                progressPending={loading}
                                progressComponent={<LoadingState title={t('loading_zones', 'Cargando zonas de distribución...')} description={t('loading_zones_sub', 'Sincronizando polígonos y rutas asignadas')} minHeight="180px" />}
                                customStyles={getDataTableStyles(isDark)}
                            />
                        </div>
                    ) : (
                        <div>
                            <Row className="g-3">
                                {/* Global Map Canvas */}
                                <Col lg={9} md={8}>
                                    <div style={{ height: '640px', borderRadius: '14px', overflow: 'hidden', border: isDark ? '2px solid #233052' : '2px solid #e2e8f0', position: 'relative' }}>
                                        <MapContainer 
                                            center={BARINAS_CENTER}
                                            zoom={12} 
                                            style={{ height: '100%', width: '100%' }}
                                        >
                                            <TileLayer
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                attribution='&copy; OpenStreetMap'
                                            />
                                            
                                            <MultiZoneMapViewController 
                                                zones={filteredZones} 
                                                selectedZoneId={selectedMapZoneId} 
                                            />

                                            {filteredZones.map((zone, idx) => {
                                                const poly = normalizePolygonPoints(zone.polygon);
                                                if (poly.length < 3) return null;
                                                const palette = getZoneColor(idx);
                                                const isSelected = selectedMapZoneId && String(zone.id) === String(selectedMapZoneId);

                                                return (
                                                    <Polygon
                                                        key={`map-zone-${zone.id}`}
                                                        positions={poly}
                                                        pathOptions={{
                                                            color: isSelected ? '#ef4444' : palette.color,
                                                            fillColor: palette.color,
                                                            fillOpacity: isSelected ? 0.45 : 0.28,
                                                            weight: isSelected ? 4 : 2.5
                                                        }}
                                                        eventHandlers={{
                                                            click: () => setSelectedMapZoneId(zone.id)
                                                        }}
                                                    >
                                                        <Tooltip sticky direction="top">
                                                            <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                                                                {zone.name} ({zone.code})
                                                            </div>
                                                            <div style={{ fontSize: '0.74rem', color: '#475569' }}>
                                                                Preventa: {zone.vendedor?.nombre_completo || 'Sin Vendedor'}
                                                            </div>
                                                        </Tooltip>

                                                        <Popup>
                                                            <div style={{ minWidth: '220px', padding: '4px' }}>
                                                                <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                                                                    <span 
                                                                        style={{ 
                                                                            width: '12px', 
                                                                            height: '12px', 
                                                                            borderRadius: '50%', 
                                                                            backgroundColor: palette.color,
                                                                            display: 'inline-block' 
                                                                        }} 
                                                                    />
                                                                    <div>
                                                                        <strong style={{ fontSize: '0.95rem' }}>{zone.name}</strong>
                                                                        <span className="badge bg-secondary ms-1">{zone.code}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="mb-2 small">
                                                                    <strong><i className="mdi mdi-account-tie text-primary me-1"></i> Vendedor:</strong>{' '}
                                                                    {zone.vendedor?.nombre_completo || <span className="text-muted">Sin Asignar</span>}
                                                                </div>
                                                                <div className="mb-3 small">
                                                                    <strong><i className="mdi mdi-store text-purple me-1"></i> Mercaderista:</strong>{' '}
                                                                    {zone.mercaderista?.nombre_completo || <span className="text-muted">Sin Asignar</span>}
                                                                </div>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-sm btn-primary w-100 py-1"
                                                                    style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}
                                                                    onClick={() => handleShowModal(zone)}
                                                                >
                                                                    <i className="mdi mdi-pencil me-1"></i> Editar Geocerca
                                                                </button>
                                                            </div>
                                                        </Popup>
                                                    </Polygon>
                                                );
                                            })}
                                        </MapContainer>
                                    </div>
                                </Col>

                                {/* Sidebar: Interactive Legend & Territorial Focus */}
                                <Col lg={3} md={4}>
                                    <div 
                                        className="p-3 rounded-3 d-flex flex-column h-100" 
                                        style={{ 
                                            backgroundColor: isDark ? '#10172d' : '#f8fafc', 
                                            border: isDark ? '1px solid #233052' : '1px solid #e2e8f0',
                                            maxHeight: '640px'
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.9rem' }}>
                                                <i className="mdi mdi-layers-outline me-1 text-primary"></i> Territorios ({filteredZones.filter(z => normalizePolygonPoints(z.polygon).length >= 3).length})
                                            </h6>
                                            {selectedMapZoneId && (
                                                <button 
                                                    type="button" 
                                                    className="btn btn-link text-decoration-none p-0 text-primary fw-semibold" 
                                                    style={{ fontSize: '0.75rem' }}
                                                    onClick={() => setSelectedMapZoneId(null)}
                                                >
                                                    Centrar Todo
                                                </button>
                                            )}
                                        </div>

                                        <div className="d-flex flex-column gap-2" style={{ overflowY: 'auto', paddingRight: '4px' }}>
                                            {filteredZones.map((zone, idx) => {
                                                const poly = normalizePolygonPoints(zone.polygon);
                                                const hasPoly = poly.length >= 3;
                                                const palette = getZoneColor(idx);
                                                const isSelected = selectedMapZoneId && String(zone.id) === String(selectedMapZoneId);

                                                return (
                                                    <div
                                                        key={`legend-${zone.id}`}
                                                        onClick={() => hasPoly && setSelectedMapZoneId(isSelected ? null : zone.id)}
                                                        className="p-2 rounded-2 transition-all"
                                                        style={{
                                                            cursor: hasPoly ? 'pointer' : 'default',
                                                            opacity: hasPoly ? 1 : 0.45,
                                                            backgroundColor: isSelected 
                                                                ? (isDark ? 'rgba(24, 62, 235, 0.25)' : 'rgba(24, 62, 235, 0.1)') 
                                                                : (isDark ? '#161e38' : '#ffffff'),
                                                            border: isSelected 
                                                                ? '1.5px solid #183eeb' 
                                                                : (isDark ? '1px solid #233052' : '1px solid #e2e8f0'),
                                                            boxShadow: isSelected ? '0 2px 8px rgba(24, 62, 235, 0.2)' : 'none'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                                            <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                                <span 
                                                                    style={{ 
                                                                        width: '10px', 
                                                                        height: '10px', 
                                                                        borderRadius: '50%', 
                                                                        backgroundColor: hasPoly ? palette.color : '#94a3b8',
                                                                        flexShrink: 0
                                                                    }} 
                                                                />
                                                                <span className="fw-semibold small text-truncate" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                                                                    {zone.name}
                                                                </span>
                                                            </div>
                                                            <span className="badge rounded-pill" style={{ fontSize: '0.65rem', backgroundColor: palette.bg, color: palette.color, border: `1px solid ${palette.border}` }}>
                                                                {zone.code}
                                                            </span>
                                                        </div>

                                                        <div className="d-flex justify-content-between align-items-center mt-1 pt-1 border-top" style={{ borderColor: isDark ? '#233052' : '#f1f5f9', fontSize: '0.72rem' }}>
                                                            <span className="text-muted text-truncate" style={{ maxWidth: '140px' }}>
                                                                <i className="mdi mdi-account-tie me-1"></i>
                                                                {zone.vendedor?.nombre_completo || 'Sin vendedor'}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleShowModal(zone);
                                                                }}
                                                                className="btn btn-xs btn-link p-0 text-primary text-decoration-none"
                                                                title="Editar geocerca"
                                                            >
                                                                <i className="mdi mdi-pencil"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Creación/Edición */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
                    <Modal.Title style={{ color: '#ffffff', fontWeight: 'bold' }}>
                        {editingId ? t('modal_edit_zone', 'Editar Zona de Distribución') : t('modal_new_zone', 'Nueva Zona de Distribución')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
                    <Form onSubmit={handleSubmit}>
                        {/* Conflict Error Banner */}
                        {conflictError && (
                            <div 
                                className="d-flex align-items-start gap-2 p-3 mb-3 rounded border"
                                style={{ 
                                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2', 
                                    borderColor: '#ef4444', 
                                    color: isDark ? '#fca5a5' : '#991b1b',
                                    borderRadius: '10px'
                                }}
                            >
                                <i className="mdi mdi-alert-octagon fs-5 flex-shrink-0 mt-0" style={{ color: '#ef4444' }}></i>
                                <div>
                                    <strong className="d-block mb-1">¡Conflicto Territorial Detectado!</strong>
                                    <span style={{ fontSize: '0.82rem' }}>{conflictError}</span>
                                </div>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <Form.Label style={{ color: isDark ? '#94a3b8' : '#010028', fontWeight: 'bold' }}>{t('th_zone_code', 'Código de Zona')}</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    required 
                                    placeholder="Ej: ZON-NTE"
                                    value={formData.code} 
                                    onChange={e => setFormData({...formData, code: e.target.value})} 
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                        color: isDark ? '#ffffff' : '#010028',
                                        border: isDark ? '1px solid #2a3759' : '1px solid #183eeb', 
                                        borderRadius: '10px' 
                                    }} 
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Label style={{ color: isDark ? '#94a3b8' : '#010028', fontWeight: 'bold' }}>{t('th_zone_name', 'Nombre de Zona')}</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    required 
                                    placeholder="Ej: Zona Norte"
                                    value={formData.name} 
                                    onChange={e => setFormData({...formData, name: e.target.value})} 
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                        color: isDark ? '#ffffff' : '#010028',
                                        border: isDark ? '1px solid #2a3759' : '1px solid #183eeb', 
                                        borderRadius: '10px' 
                                    }} 
                                />
                            </div>
                        </div>

                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Label style={{ color: isDark ? '#94a3b8' : '#010028', fontWeight: 'bold' }}>
                                    <i className="mdi mdi-account-tie mr-1 text-primary"></i> {t('th_assigned_vendor', 'Vendedor Asignado')}
                                </Form.Label>
                                <Form.Select 
                                    value={formData.vendedor_id} 
                                    onChange={e => setFormData({...formData, vendedor_id: e.target.value})}
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                        color: isDark ? '#ffffff' : '#010028',
                                        border: isDark ? '1px solid #2a3759' : '1px solid #183eeb', 
                                        borderRadius: '10px' 
                                    }}
                                >
                                    <option value="" style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', color: isDark ? '#ffffff' : '#010028' }}>{t('select_vendor_placeholder', 'Seleccione un vendedor...')}</option>
                                    {vendedores.map(v => {
                                        const isCurrentTitular = editingId && (zones || []).some(z => String(z.id) === String(editingId) && String(z.vendedor_id) === String(v.id));
                                        const otherAssignedZone = (zones || []).find(z => String(z.vendedor_id) === String(v.id) && (!editingId || String(z.id) !== String(editingId)));
                                        let tag = ' [DISPONIBLE]';
                                        if (isCurrentTitular) {
                                            tag = ' [TITULAR ACTUAL]';
                                        } else if (otherAssignedZone) {
                                            tag = ` [ASIGNADO: ${otherAssignedZone.code || otherAssignedZone.name}]`;
                                        }
                                        return (
                                            <option key={v.id} value={v.id} style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', color: isDark ? '#ffffff' : '#010028' }}>
                                                {v.nombre_completo}{tag}
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                                {selectedVendorAssignedOtherZone ? (
                                    <div 
                                        className="d-flex align-items-start gap-2 p-2 mt-2 rounded border"
                                        style={{ 
                                            backgroundColor: isDark ? 'rgba(255, 59, 59, 0.12)' : '#fff1f2', 
                                            borderColor: '#ff3b3b', 
                                            color: isDark ? '#fca5a5' : '#991b1b',
                                            fontSize: '0.74rem',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <i className="mdi mdi-alert-circle fs-6 flex-shrink-0 mt-0" style={{ color: '#ff3b3b' }}></i>
                                        <div>
                                            <strong>Reasignación Automática:</strong> Este vendedor ya es titular de <strong>{selectedVendorAssignedOtherZone.name} ({selectedVendorAssignedOtherZone.code})</strong>. Al guardar, será transferido como titular a esta zona y liberará su zona anterior.
                                        </div>
                                    </div>
                                ) : (
                                    <small className="text-muted d-block mt-1">Responsable de preventa y pedidos en esta zona.</small>
                                )}
                            </Col>
                            <Col md={6}>
                                <Form.Label style={{ color: isDark ? '#94a3b8' : '#010028', fontWeight: 'bold' }}>
                                    <i className="mdi mdi-store mr-1" style={{ color: '#7c3aed' }}></i> {t('th_assigned_merchandiser', 'Mercaderista Asignado')}
                                </Form.Label>
                                <Form.Select 
                                    value={formData.mercaderista_id} 
                                    onChange={e => setFormData({...formData, mercaderista_id: e.target.value})}
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                        color: isDark ? '#ffffff' : '#010028',
                                        border: isDark ? '1px solid #2a3759' : '1px solid #7c3aed', 
                                        borderRadius: '10px' 
                                    }}
                                >
                                    <option value="" style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', color: isDark ? '#ffffff' : '#010028' }}>{t('select_merchandiser_placeholder', 'Seleccione un mercaderista...')}</option>
                                    {mercaderistas.map(m => {
                                        const isCurrentTitular = editingId && (zones || []).some(z => String(z.id) === String(editingId) && String(z.mercaderista_id) === String(m.id));
                                        const otherAssignedZone = (zones || []).find(z => String(z.mercaderista_id) === String(m.id) && (!editingId || String(z.id) !== String(editingId)));
                                        let tag = ' [DISPONIBLE]';
                                        if (isCurrentTitular) {
                                            tag = ' [TITULAR ACTUAL]';
                                        } else if (otherAssignedZone) {
                                            tag = ` [ASIGNADO: ${otherAssignedZone.code || otherAssignedZone.name}]`;
                                        }
                                        return (
                                            <option key={m.id} value={m.id} style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', color: isDark ? '#ffffff' : '#010028' }}>
                                                {m.nombre_completo}{tag}
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                                {selectedMerchAssignedOtherZone ? (
                                    <div 
                                        className="d-flex align-items-start gap-2 p-2 mt-2 rounded border"
                                        style={{ 
                                            backgroundColor: isDark ? 'rgba(255, 59, 59, 0.12)' : '#fff1f2', 
                                            borderColor: '#ff3b3b', 
                                            color: isDark ? '#fca5a5' : '#991b1b',
                                            fontSize: '0.74rem',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <i className="mdi mdi-alert-circle fs-6 flex-shrink-0 mt-0" style={{ color: '#ff3b3b' }}></i>
                                        <div>
                                            <strong>Reasignación Automática:</strong> Este mercaderista ya es titular de <strong>{selectedMerchAssignedOtherZone.name} ({selectedMerchAssignedOtherZone.code})</strong>. Al guardar, será transferido como titular a esta zona y liberará su zona anterior.
                                        </div>
                                    </div>
                                ) : (
                                    <small className="text-muted d-block mt-1">Responsable de trade marketing, anaquel y auditoría.</small>
                                )}
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-end mb-2">
                                <Form.Label style={{ color: isDark ? '#94a3b8' : '#010028', fontWeight: 'bold', margin: 0 }}>
                                    {t('geographic_area_map', 'Área Geográfica (Mapa)')}
                                </Form.Label>
                                <div className="d-flex gap-2">
                                    <button type="button" onClick={removeLastPoint} disabled={formData.polygon.length === 0} className="btn btn-sm btn-outline-warning" style={{ borderRadius: '8px' }}>
                                        <i className="mdi mdi-undo"></i> {t('undo_point', 'Deshacer Punto')}
                                    </button>
                                    <button type="button" onClick={clearPolygon} disabled={formData.polygon.length === 0} className="btn btn-sm btn-outline-danger" style={{ borderRadius: '8px' }}>
                                        <i className="mdi mdi-delete-empty"></i> {t('clear_map', 'Limpiar Mapa')}
                                    </button>
                                </div>
                            </div>
                            <div className="alert alert-warning py-2 small mb-3" style={{ borderRadius: '8px', border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid #ffecb5', backgroundColor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fff3cd', color: isDark ? '#fbbf24' : '#664d03' }}>
                                <strong><i className="mdi mdi-lightbulb-on text-warning me-1"></i> GUÍA Y REFERENCIA DE TERRITORIOS:</strong>
                                <ul className="mb-0 mt-1 pl-3">
                                    <li>Las <strong>zonas vecinas</strong> se muestran con contorno discontinuo para que veas sus límites y evites invadirlas.</li>
                                    <li>Haz clic en el mapa para ir agregando vértices a esta zona.</li>
                                    <li><strong>¿Punto mal ubicado?</strong> Arrástralo a su posición correcta. Para borrar un vértice específico, haz <strong>clic derecho</strong> sobre él.</li>
                                </ul>
                            </div>
                            
                            <div style={{ height: '380px', border: isDark ? '2px solid #2a3759' : '2px solid #183eeb', borderRadius: '12px', overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                                <MapContainer 
                                    center={BARINAS_CENTER}
                                    zoom={13} 
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; OpenStreetMap'
                                    />
                                    
                                    <MapViewController polygon={formData.polygon} />

                                    <MapClickHandler 
                                        polygon={formData.polygon} 
                                        setPolygon={(newPoly) => {
                                            setConflictError(null);
                                            setFormData({...formData, polygon: newPoly});
                                        }} 
                                    />

                                    {/* Neighboring zones rendered as reference boundaries */}
                                    {zones
                                        .filter(z => !editingId || String(z.id) !== String(editingId))
                                        .map((neighborZone, nIdx) => {
                                            const nPoints = normalizePolygonPoints(neighborZone.polygon);
                                            if (nPoints.length < 3) return null;
                                            const theme = getZoneColor(nIdx);
                                            return (
                                                <Polygon
                                                    key={`neighbor-${neighborZone.id}`}
                                                    positions={nPoints}
                                                    pathOptions={{
                                                        color: theme.color,
                                                        fillColor: theme.color,
                                                        fillOpacity: 0.10,
                                                        weight: 1.5,
                                                        dashArray: '5, 5'
                                                    }}
                                                >
                                                    <Tooltip sticky direction="top">
                                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                            Zona Vecina: {neighborZone.name} ({neighborZone.code})
                                                        </span>
                                                    </Tooltip>
                                                </Polygon>
                                            );
                                        })
                                    }

                                    {formData.polygon.length > 0 && (
                                        <>
                                            {/* Draw markers for each point to make it visible and draggable */}
                                            {formData.polygon.map((pos, idx) => (
                                                <Marker 
                                                    key={idx} 
                                                    position={pos} 
                                                    draggable={true}
                                                    eventHandlers={{
                                                        dragend: (e) => {
                                                            setConflictError(null);
                                                            const newPos = e.target.getLatLng();
                                                            const newPolygon = [...formData.polygon];
                                                            newPolygon[idx] = [newPos.lat, newPos.lng];
                                                            setFormData({...formData, polygon: newPolygon});
                                                        },
                                                        contextmenu: () => {
                                                            setConflictError(null);
                                                            const newPolygon = [...formData.polygon];
                                                            newPolygon.splice(idx, 1);
                                                            setFormData({...formData, polygon: newPolygon});
                                                        }
                                                    }}
                                                />
                                            ))}
                                            
                                            {/* Draw the polygon shape being edited */}
                                            {orderedPolygon.length > 2 && (
                                                <Polygon 
                                                    positions={orderedPolygon} 
                                                    pathOptions={{ color: '#183eeb', fillColor: '#183eeb', fillOpacity: 0.35, weight: 3 }} 
                                                />
                                            )}
                                        </>
                                    )}
                                </MapContainer>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-3 mt-4 pt-3" style={{ borderTop: isDark ? '1px solid #233052' : '2px solid #f1f4ff' }}>
                            <button type="button" className="btn btn-light px-4" onClick={() => setShowModal(false)} style={{ borderRadius: '10px', fontWeight: '600', backgroundColor: isDark ? '#233052' : '#f8f9fa', color: isDark ? '#ffffff' : '#010028', border: 'none' }} disabled={processing}>
                                {t('btn_cancel', 'Cancelar')}
                            </button>
                            <button type="submit" className="btn btn-primary px-4" style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '10px', fontWeight: 'bold' }} disabled={processing}>
                                {processing ? <Spinner size="sm" /> : t('btn_save_zone', 'Guardar Zona')}
                            </button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* MODAL DE RESULTADO DE SINCRONIZACIÓN TERRITORIAL */}
            <Modal 
                show={showSyncModal} 
                onHide={() => setShowSyncModal(false)} 
                size="lg" 
                centered
            >
                <Modal.Header 
                    closeButton 
                    closeVariant={isDark ? 'white' : undefined} 
                    style={{ backgroundColor: '#183eeb', color: '#ffffff', borderBottom: 'none' }}
                >
                    <Modal.Title className="d-flex align-items-center">
                        <i className="mdi mdi-map-marker-distance me-2 fs-4"></i>
                        <span style={{ fontWeight: 'bold' }}>Sincronización Territorial de Clientes</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body 
                    className="p-4" 
                    style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}
                >
                    {syncResult && (
                        <>
                            <div className="mb-4">
                                <h6 className="fw-bold mb-1" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                                    Zona: {syncResult.zoneName} ({syncResult.zoneCode})
                                </h6>
                                <p className="text-muted small mb-0">
                                    La geocerca fue actualizada y el motor geoespacial sincronizó automáticamente los clientes según sus coordenadas geográficas exactas:
                                </p>
                            </div>

                            {/* 1. Clientes Absorbidos */}
                            {syncResult.absorbed > 0 && (
                                <div 
                                    className="p-3 mb-3 rounded border" 
                                    style={{ 
                                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5', 
                                        borderColor: '#10b981', 
                                        borderRadius: '10px' 
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="fw-bold d-flex align-items-center" style={{ color: isDark ? '#34d399' : '#065f46', fontSize: '0.95rem' }}>
                                            <i className="mdi mdi-account-plus me-2 fs-5"></i>
                                            {syncResult.absorbed} Cliente(s) Absorbido(s) al Territorio
                                        </span>
                                        <Badge bg="success">{syncResult.absorbed}</Badge>
                                    </div>
                                    <p className="small mb-2" style={{ color: isDark ? '#a7f3d0' : '#047857' }}>
                                        Estaban sin zona y sus coordenadas ahora caen dentro de esta geocerca. Fueron asignados a esta zona y a su vendedor titular <strong>({syncResult.vendedorName})</strong>:
                                    </p>
                                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                        <ul className="list-unstyled mb-0">
                                            {(syncResult.absorbed_clients || []).map((c, i) => (
                                                <li 
                                                    key={c.id || i} 
                                                    className="py-2 px-3 rounded mb-1 d-flex align-items-center justify-content-between shadow-sm" 
                                                    style={{ 
                                                        backgroundColor: isDark ? '#111827' : '#ffffff', 
                                                        border: isDark ? '1px solid #1f2937' : '1px solid #e2e8f0', 
                                                        fontSize: '0.84rem' 
                                                    }}
                                                >
                                                    <div>
                                                        <strong style={{ color: isDark ? '#ffffff' : '#010028' }}>{c.name}</strong>
                                                        <span className="text-muted ms-2">({c.rif})</span>
                                                    </div>
                                                    <span className="badge bg-success bg-opacity-25 text-success">
                                                        <i className="mdi mdi-check me-1"></i> Asignado
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* 2. Clientes Expulsados (Quedaron Sin Zona) */}
                            {syncResult.expelled > 0 && (
                                <div 
                                    className="p-3 mb-3 rounded border" 
                                    style={{ 
                                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#fff1f2', 
                                        borderColor: '#ef4444', 
                                        borderRadius: '10px' 
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="fw-bold d-flex align-items-center" style={{ color: isDark ? '#f87171' : '#991b1b', fontSize: '0.95rem' }}>
                                            <i className="mdi mdi-alert-circle-outline me-2 fs-5"></i>
                                            {syncResult.expelled} Cliente(s) Fuera de Geocerca (Pasan a 'Sin Zona')
                                        </span>
                                        <Badge bg="danger">{syncResult.expelled}</Badge>
                                    </div>
                                    <p className="small mb-2" style={{ color: isDark ? '#fecaca' : '#b91c1c' }}>
                                        Pertenecían a esta zona pero sus coordenadas quedaron fuera de la nueva geocerca. Pasaron a estatus territorial <strong>Sin Zona</strong> y requieren auditoría o ampliación del polígono:
                                    </p>
                                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                        <ul className="list-unstyled mb-2">
                                            {(syncResult.expelled_clients || []).map((c, i) => (
                                                <li 
                                                    key={c.id || i} 
                                                    className="py-2 px-3 rounded mb-1 d-flex align-items-center justify-content-between shadow-sm" 
                                                    style={{ 
                                                        backgroundColor: isDark ? '#111827' : '#ffffff', 
                                                        border: isDark ? '1px solid #1f2937' : '1px solid #e2e8f0', 
                                                        fontSize: '0.84rem' 
                                                    }}
                                                >
                                                    <div>
                                                        <strong style={{ color: '#ef4444' }}>{c.name}</strong>
                                                        <span className="text-muted ms-2">({c.rif})</span>
                                                    </div>
                                                    <span className="badge bg-danger bg-opacity-25 text-danger d-inline-flex align-items-center gap-1">
                                                        <i className="mdi mdi-map-marker-off-outline"></i> Sin Zona
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="d-flex justify-content-end mt-2">
                                        <Button 
                                            size="sm" 
                                            variant="danger" 
                                            style={{ borderRadius: '8px', fontSize: '0.82rem', fontWeight: 'bold' }}
                                            onClick={() => {
                                                setShowSyncModal(false);
                                                navigate('/general-pages/customer-listing?zone_id=unassigned');
                                            }}
                                        >
                                            <i className="mdi mdi-account-search me-1"></i> Ver en Directorio de Clientes (Filtro Sin Zona)
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* 3. Clientes Reubicados en Zonas Vecinas */}
                            {syncResult.reassigned_other > 0 && (
                                <div 
                                    className="p-3 mb-3 rounded border" 
                                    style={{ 
                                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff', 
                                        borderColor: '#3b82f6', 
                                        borderRadius: '10px' 
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="fw-bold d-flex align-items-center" style={{ color: isDark ? '#60a5fa' : '#1e40af', fontSize: '0.95rem' }}>
                                            <i className="mdi mdi-transit-transfer me-2 fs-5"></i>
                                            {syncResult.reassigned_other} Cliente(s) Reubicado(s) en Zonas Vecinas
                                        </span>
                                        <Badge bg="primary">{syncResult.reassigned_other}</Badge>
                                    </div>
                                    <p className="small mb-2" style={{ color: isDark ? '#bfdbfe' : '#1d4ed8' }}>
                                        Salieron de este polígono pero cayeron dentro del polígono de una zona contigua:
                                    </p>
                                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                        <ul className="list-unstyled mb-0">
                                            {(syncResult.reassigned_clients || []).map((c, i) => (
                                                <li 
                                                    key={c.id || i} 
                                                    className="py-2 px-3 rounded mb-1 d-flex align-items-center justify-content-between shadow-sm" 
                                                    style={{ 
                                                        backgroundColor: isDark ? '#111827' : '#ffffff', 
                                                        border: isDark ? '1px solid #1f2937' : '1px solid #e2e8f0', 
                                                        fontSize: '0.84rem' 
                                                    }}
                                                >
                                                    <div>
                                                        <strong style={{ color: isDark ? '#ffffff' : '#010028' }}>{c.name}</strong>
                                                        <span className="text-muted ms-2">({c.rif})</span>
                                                    </div>
                                                    <span className="badge bg-primary bg-opacity-25 text-primary">
                                                        Zona: {c.new_zone}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', borderTop: isDark ? '1px solid #233052' : '2px solid #f1f4ff' }}>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowSyncModal(false)} 
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                    >
                        Cerrar
                    </Button>
                    {syncResult?.expelled > 0 && (
                        <Button 
                            style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}
                            onClick={() => {
                                setShowSyncModal(false);
                                navigate('/general-pages/customer-listing?zone_id=unassigned');
                            }}
                        >
                            <i className="mdi mdi-arrow-right me-1"></i> Ir a Clientes Sin Zona
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ZonesManager;
